import { computed, getCurrentInstance, onActivated, onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {  MsgBox } from '@/util/msgutil/msgBox';

/**
 * @typedef {Object} MenuItem
 * @property {string} menuId - 菜单ID
 * @property {string} menuName - 菜单名称
 * @property {string} menuIcon - 菜单图标
 * @property {string} menuType - 菜单类型（1=文件夹，2=菜单）
 * @property {string} menuUrl - 菜单URL
 * @property {boolean} isShow - 是否显示
 * @property {number} sortNum - 菜单序号
 * @property {string|null} parentMenuId - 父菜单ID
 * @property {MenuItem[]} children - 子菜单列表
 * @property {boolean} _isModified - 前端临时标记是否修改
 */

export function setupEditMenu() {
    const { $request } = getCurrentInstance().appContext.config.globalProperties;

    // 核心数据：仅维护最终操作的菜单列表（唯一数据源）
    const menuList = ref(/** @type {MenuItem[]} */ ([]));
    const loading = ref(false);
    const showModal = ref(false); // 弹窗显隐
    const isEdit = ref(false); // 是否编辑模式
    const currentEditId = ref(null); // 当前编辑的菜单ID
    const parentMenuId = ref(null); // 父菜单ID（添加子菜单时用）
    const deletedMenuIds = ref([]);// 存储被删除的菜单ID（每次新增会生成新的id不会重复删）
    const sortablesType  = ref(false);//是否加载 Sortable

    // 表单数据
    const formData = ref(/** @type {Partial<MenuItem>} */({
        menuName: '',
        menuIcon: '',
        menuType: '1', // 1=文件夹，2=菜单
        menuUrl: '',
        isShow: '0',
        sortNum: '',
        parentMenuId: null
    }));

    // 计算属性：判断menuList是否有修改（仅前端UI提示，非核心）
    const hasChanges = computed(() => {
        //新增或修改菜单时 _isModified = true 标识为有调整
        // 递归检查所有层级菜单的 _isModified
        const checkAllModified = (list) => {
            for (const menu of list) {
                if (menu._isModified) return true;
                // 递归检查子菜单
                if (menu.children?.length && checkAllModified(menu.children)) {
                    return true;
                }
            }
            return false;
        };
        const hasModifyOrAdd = checkAllModified(menuList.value);
        //是否有删除操作
        const hasDelete = deletedMenuIds.value && deletedMenuIds.value.length > 0;
        return hasModifyOrAdd  || hasDelete;
    });

    // 生成临时菜单ID（时间戳+随机数，长度<24位，保证不重复）
    // 13位时间戳  + 7位随机数 = 总长度13+7=20位 <24位
    const generateTempMenuId = () => {
        const timestamp = Date.now(); // 13位数字
        const random7Str = Math.random().toString(36).slice(2, 9);// 7位随机字母/数字
        return `${timestamp}${random7Str}`;
    };

    // 加载菜单数据（初始化/重置时用）
    const loadMenuData = async () => {
        try {
            loading.value = true;
            sortablesType.value = false;
            const rawData = await $request.get('/menu/getALLMenu');
            // 格式化原始数据，添加_isModified标记（初始为false）
            menuList.value = (rawData.data || []).map(menu => ({
                ...menu,
                menuId: menu.menuId || generateTempMenuId(), // 改用新的临时ID生成方法
                menuName: menu.menuName || '未命名菜单',
                menuIcon: menu.menuIcon || '',
                menuType: menu.menuType || '1',
                menuUrl: menu.menuUrl || '',
                isShow: menu.isShow !== undefined ? menu.isShow : '1',
                sortNum: menu.sortNum || 0,
                parentMenuId: menu.parentMenuId || null,
                children: menu.children || [],
                _isModified: false // 标记是否被编辑过（前端临时标记）
            }));
            // 排序所有层级菜单
            sortMenuListRecursive(menuList.value);
            sortablesType.value = true;
        } catch (err) {
            MsgBox.error(`加载菜单失败: ${err.message}`);
            menuList.value = [];
        } finally {
            loading.value = false;
        }
    };

    // 递归查找菜单（按menuId，保留原有核心辅助方法）
    const findMenu = (menuId, list = menuList.value) => {
        for (const menu of list) {
            if (menu.menuId === menuId) return menu;
            if (menu.children?.length) {
                const found = findMenu(menuId, menu.children);
                if (found) return found;
            }
        }
        return null;
    };

    // 递归查找父菜单
    const findParentMenu = (parentId, list = menuList.value) => {
        for (const menu of list) {
            if (menu.menuId === parentId) return menu;
            if (menu.children?.length) {
                const found = findParentMenu(parentId, menu.children);
                if (found) return found;
            }
        }
        return null;
    };
    

    // 通用排序：按sortNum升序
    const sortMenuList = (list) => {
        if (!Array.isArray(list)) return;
        list.sort((a, b) => {
            const sortA = Number.isFinite(Number(a.sortNum)) ? Number(a.sortNum) : 0;
            const sortB = Number.isFinite(Number(b.sortNum)) ? Number(b.sortNum) : 0;
            return sortA - sortB;
        });
    };

    // 递归排序所有层级菜单
    const sortMenuListRecursive = (list) => {
        if (!Array.isArray(list)) return;
        sortMenuList(list);
        list.forEach(menu => sortMenuListRecursive(menu.children));
    };

    // 更新菜单（编辑时用）
    const updateMenu = (menuData) => {
        const menu = findMenu(menuData.menuId);
        if (menu) {
            Object.assign(menu, menuData);
            menu._isModified = true; // 标记为已修改
        }
    };

    // 删除菜单（直接修改menuList）
    const removeMenu = (menuId, list = menuList.value) => {
        for (let i = 0; i < list.length; i++) {
            if (list[i].menuId === menuId) {
                list.splice(i, 1);
                sortMenuList(list);
                return true;
            }
            if (list[i].children?.length && removeMenu(menuId, list[i].children)) {
                sortMenuList(list[i].children);
                return true;
            }
        }
        return false;
    };

    // ========== 拖拽板块 ==========
    /**
     * 处理菜单拖拽逻辑
     * @param {Object} params - 拖拽参数
     * @param {string} params.draggedMenuId - 被拖拽的菜单ID
     * @param {string|null} params.newParentMenuId - 目标父菜单ID（拖到顶级则为null）
     * @param {number} params.newIndex - 拖拽后的新索引
     */
    const handleMenuDrag = (params) => {
        const { menuId, oldParentMenuId,newParentMenuId, newIndex } = params;
        // 1. 找到被拖拽的菜单
        const draggedMenu = findMenu(menuId);
        if (!draggedMenu) return ElMessage.error('未找到被拖拽的菜单');

        // 2. 找到原父菜单 和 目标父菜单
        const oldParentMenu = findMenu(oldParentMenuId);
        const newParentMenu = newParentMenuId ? findMenu(newParentMenuId) : null;

        // 校验：目标父菜单必须是文件夹（menuType=1），顶级拖拽无需校验
        if (newParentMenu && newParentMenu.menuType !== '1') {
            return ElMessage.warning('只能拖拽到文件夹类型的菜单下');
        }

        // 3. 处理原父菜单：移除被拖拽的菜单
        if (oldParentMenu) {
            oldParentMenu.children = oldParentMenu.children.filter(item => item.menuId !== menuId);
            // oldParentMenu._isModified = true; // 标记原父菜单修改
            sortMenuList(oldParentMenu.children); // 原父菜单重新排序
        } else {
            // 原顶级菜单，从menuList移除
            menuList.value = menuList.value.filter(item => item.menuId !== menuId);
            sortMenuList(menuList.value);
        }

        // 4. 处理目标父菜单：添加被拖拽的菜单
        draggedMenu.parentMenuId = newParentMenuId; // 更新父ID
        draggedMenu._isModified = true; // 标记拖拽菜单修改

        if (newParentMenu) {
            // 拖到子菜单层级
            newParentMenu.children.splice(newIndex, 0, draggedMenu);
            // newParentMenu._isModified = true; // 标记目标父菜单修改
            // 重新设置子菜单的sortNum（按索引顺序）
            newParentMenu.children.forEach((item, index) => {
                item.sortNum = index + 1;
                item._isModified = true;
            });

            // sortMenuList(newParentMenu.children);//手动拖拽位置了不用重新排序
        } else {
            // 拖到顶级层级
            menuList.value.splice(newIndex, 0, draggedMenu);
            // 重新设置顶级菜单的sortNum
            menuList.value.forEach((item, index) => {
                item.sortNum = index + 1;
                item._isModified = true;
            });
            //赋值完重新排序
            // sortMenuList(menuList.value);
        }
    };

    // ========== 拖拽板块结束 ==========

    // 保存菜单到本地（新增/编辑统一处理）
    const saveMenuToLocal = () => {
        // 表单验证
        if (!formData.value.menuName.trim()) {
            return ElMessage.warning('请输入菜单名称！');
        }
        if (formData.value.sortNum !== '' && isNaN(Number(formData.value.sortNum))) {
            return ElMessage.warning('菜单序号必须为数字！');
        }

        let menuChildren = [];
        if (isEdit.value) {
            // 编辑模式：获取原菜单的children并保留
            const originalMenu = findMenu(currentEditId.value);
            menuChildren = originalMenu ? originalMenu.children : [];
        }
        if (formData.value.menuType === '2' && menuChildren.length > 0) {
            return ElMessage.warning('有子菜单无法设置为菜单！');
        }
        if (formData.value.menuType === '2' && !formData.value.menuUrl.trim()) {
            return ElMessage.warning('菜单类型必须填写URL！');
        }

        const menuData = {
            menuId: isEdit.value ? currentEditId.value : generateTempMenuId(), // 编辑时使用原id，新增时生成id
            menuName: formData.value.menuName.trim(),
            menuIcon: formData.value.menuIcon.trim(),
            menuType: formData.value.menuType,
            menuUrl: formData.value.menuType === '2' ? formData.value.menuUrl.trim() : '',
            isShow: formData.value.isShow,
            sortNum: formData.value.sortNum !== '' ? Number(formData.value.sortNum) : 0,
            parentMenuId: isEdit.value
                ? formData.value.parentMenuId
                : parentMenuId.value || null,
            children: menuChildren,
            _isModified: true // 新增菜单标记为已修改
        };

        let targetList = null;

        if (isEdit.value) {
            // 编辑模式：更新现有菜单
            updateMenu(menuData);
            // 找到父菜单确定排序列表
            const parentMenu = findParentMenu(menuData.parentMenuId);
            targetList = parentMenu ? parentMenu.children : menuList.value;
            ElMessage.success('菜单修改成功');
        } else {
            // 新增模式：区分顶级/子菜单
            if (menuData.parentMenuId) {
                const parentMenu = findParentMenu(menuData.parentMenuId);
                if (parentMenu) {
                    parentMenu.children.push(menuData);
                    targetList = parentMenu.children;
                    ElMessage.success('子菜单创建成功');
                } else {
                    ElMessage.error('父菜单不存在！');
                    return;
                }
            } else {
                menuList.value.push(menuData);
                targetList = menuList.value;
                ElMessage.success('主菜单创建成功');
            }
        }

        //编辑后重新排序
        if (targetList) {
            sortMenuList(targetList);
        }

        // 关闭弹窗+重置状态
        showModal.value = false;
        parentMenuId.value = null;
        formData.value = {
            menuName: '',
            menuIcon: '',
            menuType: '1',
            menuUrl: '',
            isShow: '0',
            sortNum: '',
            parentMenuId: null
        };
    };

    // 删除菜单（直接操作menuList，无changeRecord）
    const markMenuDelete = async (menu) => {
        if (!menu || !menu.menuId) return;
        try {
            await ElMessageBox.confirm('确定删除该菜单？', '删除确认', {
                type: 'warning',
                confirmButtonText: '确认',
                cancelButtonText: '取消'
            });
            // 记录被删除的ID
            if (!deletedMenuIds.value.includes(menu.menuId)) {
                deletedMenuIds.value.push(menu.menuId);
            }
            removeMenu(menu.menuId);
            ElMessage.success('菜单已删除');
        } catch (err) {
            if (err !== 'cancel') ElMessage.error('删除失败');
        }
    };

    // ========== 最终 提交/撤销 ==========

    // 提交所有修改
    const submitAllChanges = async () => {
        try {
            loading.value = true;
            // 步骤1：收集新增/修改的菜单（递归遍历，筛选_isModified=true的菜单）
            const collectModifiedMenus = (list) => {
                const result = [];
                list.forEach(menu => {
                    if (menu._isModified) {
                        // 过滤临时字段，仅传递业务字段
                        const cleanMenu = { ...menu };
                        delete cleanMenu._isModified;
                        result.push(cleanMenu);
                    }
                    // 递归收集子菜单
                    if (menu.children?.length) {
                        result.push(...collectModifiedMenus(menu.children));
                    }
                });
                return result;
            };
            const modifiedMenus = collectModifiedMenus(menuList.value);

            // 步骤2：无修改则提示
            if (modifiedMenus.length === 0 && deletedMenuIds.value.length === 0) {
                return ElMessage.info('暂无修改需要保存');
            }

            // 步骤3：构造提交数据（仅传修改/新增 + 已删除ID）
            const submitData = {
                saveOrUpdateMenus: modifiedMenus, // 新增+修改的菜单
                deleteMenuIds: deletedMenuIds.value // 被删除的菜单ID列表
            };

            // 步骤4：提交到后台
            const res = await $request.post('/menu/editMenu', submitData);
            if (res.code === 200) {
                ElMessage.success('所有修改已保存');
                // 提交成功后重置状态
                await loadMenuData();
                deletedMenuIds.value = []; // 清空删除记录
            } else {
                ElMessage.error(res.msg || '保存失败');
            }
        } catch (err) {
            ElMessage.error(`提交失败：${err.response?.data?.msg || err.message}`);
        } finally {
            loading.value = false;
        }
    };

    // 撤销修改（重新加载后台数据并清空删除记录）
    const resetChanges = async () => {
        try {
            await ElMessageBox.confirm('确定撤销所有未保存的修改？', '撤销确认', {
                type: 'info',
                confirmButtonText: '确认',
                cancelButtonText: '取消'
            });
            await loadMenuData();
            deletedMenuIds.value = []; // 清空删除记录
            ElMessage.success('已撤销所有修改');
        } catch (err) {
            if (err !== 'cancel') ElMessage.error('撤销失败');
        }
    };

    // 打开弹窗
    const openMenuModal = (menu = null, parentId = null) => {
        formData.value = {
            menuName: '',
            menuIcon: '',
            menuType: '1',
            menuUrl: '',
            isShow: '0',
            sortNum: '',
            parentMenuId: null
        };

        if (menu) {
            isEdit.value = true;
            currentEditId.value = menu.menuId;
            formData.value = {
                menuName: menu.menuName,
                menuIcon: menu.menuIcon,
                menuType: menu.menuType,
                menuUrl: menu.menuUrl,
                isShow: menu.isShow,
                sortNum: menu.sortNum || '',
                parentMenuId: menu.parentMenuId || null
            };
        } else {
            isEdit.value = false;
            currentEditId.value = null;
            parentMenuId.value = parentId;
            formData.value.parentMenuId = parentId || null;
        }

        showModal.value = true;
    };

    // 主页添加菜单
    const openAddMainMenuModal = () => openMenuModal(null, null);
    //递归页添加菜单
    const openAddSubMenuModal = (parentId) => {
        const parentMenu = findParentMenu(parentId);
        if (!parentMenu) return ElMessage.error('父菜单不存在！');
        if (parentMenu.menuType !== '1') return ElMessage.warning('仅文件夹可添加子菜单！');
        openMenuModal(null, parentId); // 打开弹窗
    };

    // 关闭弹窗（保留原有逻辑）
    const closeModal = () => {
        showModal.value = false;
        currentEditId.value = null;
        parentMenuId.value = null;
        formData.value = {
            menuName: '',
            menuIcon: '',
            menuType: '1',
            menuUrl: '',
            isShow: '0',
            sortNum: '',
            parentMenuId: null
        };
    };

    // 初始化
    onMounted(loadMenuData);
    onActivated(loadMenuData);

    // 导出方法和数据（新增handleMenuDrag）
    return {
        menuList,
        sortablesType,
        loading,
        showModal,
        isEdit,
        formData,
        hasChanges,
        openAddMainMenuModal,
        openAddSubMenuModal,
        openMenuModal,
        editMenu: openMenuModal, // 兼容原有调用
        closeModal,
        saveMenuToLocal,
        markMenuDelete,
        submitAllChanges,
        resetChanges,
        parentMenuId,
        handleMenuDrag // 导出拖拽处理方法
    };
}